import { useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { campaignInviteStopMode } from "@signalist/shared-types";

import { Sender } from "@/pages/Private/pages/List/components/CampaignOverview/components/Sender";

import { CopywritingCampaign } from "@/pages/Private/pages/List/pages/ListEditCopywriting/ListEditCopywriting";

import { ReachOutTarget } from "../reachOutTarget";
import {
	AlreadyConnectedNotice,
	deriveAlreadyConnectedNotice,
} from "./deriveAlreadyConnectedNotice";
import { useCheckCampaignConnectionsQuery } from "./campaignConnections.api";
import { buildConnectionCheckArgs, connectionCheckSenderIds } from "./connectionCheckArgs";

export interface AlreadyConnectedCheck {
	notice: AlreadyConnectedNotice;
	/** "Don't add the N connected leads": checked by default when the invite step stops for them. */
	skipConnected: boolean;
	setSkipConnected: (skip: boolean) => void;
	/** What `bulkAssign` leaves out: the senders, when the user skips the connected leads. */
	skipConnectedToUserIds?: number[];
	checkFailed: boolean;
}

/**
 * The pickers' "already connected on LinkedIn" check for the picked campaign (PREC-4426). Asked only
 * when the campaign has a "Send LinkedIn Invite" step: otherwise a connection is good news, since a
 * LinkedIn message reaches a connected lead.
 */
export function useAlreadyConnectedCheck(
	target: ReachOutTarget,
	campaign: CopywritingCampaign | null,
	override?: Sender[]
): AlreadyConnectedCheck {
	const mode = useMemo(() => campaignInviteStopMode(campaign?.initialNodes), [campaign]);
	const senderUserIds = useMemo(
		() => (campaign ? connectionCheckSenderIds(target, campaign, override) : []),
		[target, campaign, override]
	);
	const { data, isError } = useCheckCampaignConnectionsQuery(
		campaign && mode.hasInviteStep
			? buildConnectionCheckArgs(target, campaign.id, senderUserIds)
			: skipToken
	);
	// The choice belongs to the campaign it was made for; a newly picked campaign starts from its default.
	const [choice, setChoice] = useState<{ campaignId?: string; skip: boolean } | null>(null);
	const skipConnected = choice && choice.campaignId === campaign?.id ? choice.skip : mode.stops;
	const notice = mode.hasInviteStep
		? deriveAlreadyConnectedNotice(data, { bulk: target.kind === "bulk", stops: mode.stops })
		: deriveAlreadyConnectedNotice(undefined, { bulk: false, stops: false });

	return {
		notice,
		skipConnected,
		setSkipConnected: (skip) => setChoice({ campaignId: campaign?.id, skip }),
		skipConnectedToUserIds:
			notice.kind === "senderBulk" && skipConnected ? senderUserIds : undefined,
		checkFailed: mode.hasInviteStep && isError,
	};
}
